import { useState, useRef, useEffect } from 'react';
import classNames from 'classnames';


import { Box } from 'ui/box';

import MonitoringContext from 'containers/admin/clients/client/tabs-components/insights-tab/insight-details/components/context-data-panel/monitoring-context';
import ReportContext from 'containers/admin/clients/client/tabs-components/insights-tab/insight-details/components/context-data-panel/report-context';

import Icons from 'assets/icons';

import type { ContextMetadata } from 'containers/admin/clients/client/tabs-components/insights-tab/insight-details/components/context-data-panel/monitoring-context';
import type { InsightDto } from 'api/generated/schemas/insightDto';
import type { UserInsightDto } from 'api/generated/schemas/userInsightDto';

type ContextDataPanelInsight = InsightDto | UserInsightDto;

interface ContextDataPanelProps {
  insight: ContextDataPanelInsight | null | undefined;
  isAdminView?: boolean;
}

const ContextDataPanel = ({
  insight,
  isAdminView = true,
}: ContextDataPanelProps): React.JSX.Element => {
  const [isExpanded, setIsExpanded] = useState<boolean>(false);
  const [contentHeight, setContentHeight] = useState<number>(0);
  const contentRef = useRef<HTMLDivElement>(null);

  const metadata = (insight?.metadata || {}) as ContextMetadata;
  const isReport = metadata?.insightType === 'report';

  useEffect(() => {
    if (contentRef.current) {
      setContentHeight(isExpanded ? contentRef.current.scrollHeight : 0);
    }
  }, [isExpanded, metadata]);

  return (
    <Box
      sx={{
        borderWidth: '1px',
        borderColor: 'tokens.gray.200',
        borderRadius: '0.5rem',
        bgcolor: 'common.white',
      }}
    >
      <button
        type="button"
        className="w-full px-4 py-3 flex items-center gap-2 text-left transition-colors"
        onClick={() => setIsExpanded(!isExpanded)}
      >
        <span className="font-semibold text-navy-800 text-lg">
          Context and data
        </span>
        <Icons.Arrow
          customClass={classNames('size-4 transition-transform duration-200', {
            'rotate-[-90deg]': isExpanded,
            'rotate-90': !isExpanded,
          })}
        />
      </button>

      <div
        className="overflow-hidden transition-all duration-300 ease-in-out"
        style={{ height: `${contentHeight}px` }}
      >
        <Box
          ref={contentRef}
          sx={[{ px: 2, pb: 2 }, !isExpanded && { display: 'none' }]}
        >
          <Box sx={{ pt: 1 }} tabIndex={!isExpanded ? -1 : undefined}>
            {isReport ? (
              <ReportContext
                metadata={metadata}
                clientId={insight?.clientId}
                sourceId={metadata?.sourceId}
                isAdminView={isAdminView}
              />
            ) : (
              <MonitoringContext metadata={metadata} />
            )}
          </Box>
        </Box>
      </div>
    </Box>
  );
};

export default ContextDataPanel;
